// routes/Admin.jsx

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { CATEGORIES, getProducts, addProduct, deleteProduct, formatUGX } from '../api/getProducts';
import { isAdminAuthenticated, adminLogout } from './Login';

const BADGES = ['', 'new', 'hot', 'used', 'sale'];

export function Admin() {
  const navigate = useNavigate();
  const authed = isAdminAuthenticated();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [query, setQuery] = useState('');

  async function refresh() {
    setLoading(true);
    try {
      const data = await getProducts();
      setProducts(data);
      setLoadError('');
    } catch (err) {
      setLoadError(err.message || 'Could not load products.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (authed) refresh();
  }, [authed]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) => p.name.toLowerCase().includes(q) || p.cat.toLowerCase().includes(q)
    );
  }, [products, query]);

  if (!authed) {
    return <Navigate to="/login" replace />;
  }

  async function handleAddProduct(newProductData) {
    await addProduct(newProductData);
    await refresh();
    setShowModal(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this product?')) return;
    await deleteProduct(id);
    await refresh();
  }

  function handleLogout() {
    adminLogout();
    navigate('/login');
  }

  return (
    <div className="min-h-screen bg-bg px-4 py-10 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-text">Product Admin</h1>
            <p className="text-sm text-text-muted">
              {products.length} {products.length === 1 ? 'product' : 'products'} in catalog
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="rounded-full bg-accent px-4 py-2 text-sm font-extrabold text-black transition-colors hover:bg-accent/90"
            >
              + Add Product
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-bold text-text-muted transition-colors hover:border-white/20 hover:text-text"
            >
              Log out
            </button>
          </div>
        </div>

        {loadError && (
          <p className="mb-4 text-sm font-bold text-warning">
            {loadError} —{' '}
            <button type="button" onClick={refresh} className="underline">
              retry
            </button>
          </p>
        )}

        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products…"
          className="mb-6 w-full max-w-sm rounded-lg border border-white/10 bg-bg3 px-3 py-2 text-sm text-text outline-none focus:border-accent"
        />

        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-card">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs font-bold uppercase tracking-wide text-text-dim">
                <th className="px-4 py-3">Image</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Badge</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-muted">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading &&
                filtered.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 last:border-0">
                    <td className="px-4 py-3">
                      <img src={p.img} alt={p.name} className="h-10 w-10 rounded-lg object-cover" />
                    </td>
                    <td className="px-4 py-3 font-bold text-text">{p.name}</td>
                    <td className="px-4 py-3 text-text-muted">{p.cat}</td>
                    <td className="px-4 py-3 text-accent">UGX {formatUGX(p.price)}</td>
                    <td className="px-4 py-3 text-text-muted">{p.badge || '—'}</td>
                    <td className="px-4 py-3">
                      {p.stock ? (
                        <span className="text-xs font-bold text-accent">In stock</span>
                      ) : (
                        <span className="text-xs font-bold text-warning">Out</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id)}
                        className="text-xs font-bold text-text-dim hover:text-warning"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-muted">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <AddProductModal onClose={() => setShowModal(false)} onSave={handleAddProduct} />
      )}
    </div>
  );
}

function AddProductModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    name: '',
    cat: CATEGORIES.find((c) => c !== 'All') || '',
    price: '',
    spec: '',
    badge: '',
    img: '',
    hero: false,
    stock: true,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.name.trim() || !form.price || !form.img.trim()) {
      setError('Name, price, and image URL are required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await onSave({
        name: form.name.trim(),
        cat: form.cat,
        price: Number(form.price),
        spec: form.spec.trim(),
        badge: form.badge,
        img: form.img.trim(),
        hero: form.hero,
        stock: form.stock,
      });
    } catch (err) {
      setError(err.message || 'Could not save product.');
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-black text-text">Add Product</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-dim hover:text-text"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-bg3 px-3 py-2 text-sm text-text outline-none focus:border-accent"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select
                value={form.cat}
                onChange={(e) => update('cat', e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-bg3 px-3 py-2 text-sm text-text outline-none focus:border-accent"
              >
                {CATEGORIES.filter((c) => c !== 'All').map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Price (UGX)">
              <input
                type="number"
                min="0"
                value={form.price}
                onChange={(e) => update('price', e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-bg3 px-3 py-2 text-sm text-text outline-none focus:border-accent"
              />
            </Field>
          </div>

          <Field label="Spec / description">
            <textarea
              value={form.spec}
              onChange={(e) => update('spec', e.target.value)}
              rows={2}
              className="w-full resize-none rounded-lg border border-white/10 bg-bg3 px-3 py-2 text-sm text-text outline-none focus:border-accent"
            />
          </Field>

          <Field label="Image URL">
            <input
              type="text"
              value={form.img}
              onChange={(e) => update('img', e.target.value)}
              placeholder="https://…"
              className="w-full rounded-lg border border-white/10 bg-bg3 px-3 py-2 text-sm text-text outline-none focus:border-accent"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Badge">
              <select
                value={form.badge}
                onChange={(e) => update('badge', e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-bg3 px-3 py-2 text-sm text-text outline-none focus:border-accent"
              >
                {BADGES.map((b) => (
                  <option key={b || 'none'} value={b}>
                    {b ? b.charAt(0).toUpperCase() + b.slice(1) : 'None'}
                  </option>
                ))}
              </select>
            </Field>

            <div className="flex flex-col justify-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-sm text-text-muted">
                <input
                  type="checkbox"
                  checked={form.stock}
                  onChange={(e) => update('stock', e.target.checked)}
                />
                In stock
              </label>
              <label className="flex items-center gap-2 text-sm text-text-muted">
                <input
                  type="checkbox"
                  checked={form.hero}
                  onChange={(e) => update('hero', e.target.checked)}
                />
                Featured (hero)
              </label>
            </div>
          </div>

          {error && <p className="text-xs font-bold text-warning">{error}</p>}

          <div className="mt-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-bold text-text-muted hover:border-white/20 hover:text-text"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-accent px-4 py-2 text-sm font-extrabold text-black hover:bg-accent/90 disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-text-dim">
        {label}
      </label>
      {children}
    </div>
  );
}
